import { useId, useMemo, useRef, useState } from "react";
import { type CSSObject } from "@emotion/react";
import { IonDatetime, IonDatetimeButton, IonModal } from "@ionic/react";
import { DateTime } from "luxon";

import { translate } from "~components/string-table";
import { Colors } from "~foundations";
import { formatDate } from "~utils";

import { isWeekday, isWithinDateRange, transformDateRanges } from "./utils";

/***** Types *****/
/**
 * Disable all dates BEFORE `end` exclusively.
 * @example
 * const disabledDateRange = [
 *   {
 *     end: "2020-01-01",
 *     label: "All dates before January 1st, 2020"
 *   }
 * ]
 */
type DisableAllDatesBeforeEnd = {
  label?: string;
  end: string;
};

/**
 * Disable all dates AFTER `start` inclusively.
 * @example
 * const disabledDateRange = [
 *   {
 *     start: "2020-01-01",
 *     label: "All dates after January 1st, 2020"
 *   }
 * ]
 */
type DisableAllDatesAfterStart = {
  label?: string;
  start: string;
};

/**
 * Disable all dates in the range `[start, end]` inclusive and exclusive.
 * @example
 * const disabledDateRange = [
 *   {
 *     start: "2020-01-01",
 *     end: "2020-01-05",
 *     label: "All dates between January 1st, 2020 and January 4th, 2020"
 *   }
 * ]
 */
type DisableAllDatesInRange = {
  label?: string;
  start: string;
  end: string;
};

/**
 * Defined the `disabledDateRanges` prop which allows:
 * 1. Disabling only all dates before the END date inclusively
 * 2. Disabling only all dates after the START date inclusively
 * 3. Disabling all dates in the range `[start, end][]` inclusively
 * 4. Disabling #1 and #2
 * 5. Disabling #1, #2, and #3.
 * 6. Disabling #2 and #3.
 */
export type DisabledDateRanges =
  | [DisableAllDatesBeforeEnd]
  | [DisableAllDatesAfterStart]
  | Array<DisableAllDatesInRange>
  | [DisableAllDatesBeforeEnd, ...Array<DisableAllDatesInRange>]
  | [DisableAllDatesBeforeEnd, ...Array<DisableAllDatesInRange>, DisableAllDatesAfterStart]
  | [...Array<DisableAllDatesInRange>, DisableAllDatesAfterStart];

export type DateInputProps = {
  /**
   * The ISO-8601 formatted string, the YYYY-MM-DD values will used, everything
   * else including time and timezone will be ignored.
   *
   * TODO: Should this be a DateTime object vs a string?
   *
   * @example
   * "2020-01-01" will become "2020-01-01"
   * "2020-01-01T01:01:01:000Z" will become "2020-01-01"
   */
  value?: string;
  /**
   * Handlers called when a Date is selected, including the same date.
   *
   * @param datetime The ISO-8601 Date string "2020-01-01"
   */
  onChange?: (datetime: string) => void;
  // TODO: Add example
  disabledDateRanges?: DisabledDateRanges;

  /**
   * Shows the input as a read only field that cannot be edited.
   * @default false
   */
  isReadOnly?: boolean;
  /**
   * Adds strikethrough to the text
   *
   * TODO: Might want to review `isStrikethrough`, `isDisabled` and `isReadOnly`
   * since they could all be true at the same time.
   *
   * @default false
   */
  isStrikethrough?: boolean;
  /**
   * When `true`, will disable button
   *
   * @default false
   */
  isDisabled?: boolean;

  /**
   * When `true`, will fill out the horizontal space
   *
   * @default false
   */
  isFluid?: boolean;
};

export function DateInput(props: DateInputProps) {
  const {
    value,
    disabledDateRanges = [],
    onChange,
    isReadOnly = false,
    isStrikethrough = false,
    isFluid = false,
    isDisabled = false,
  } = props;

  /*** Hooks ***/
  const id = useId();
  // Ref to controlling the IonDateTime Modal
  const datetime = useRef<null | HTMLIonDatetimeElement>(null);

  /*** State ***/
  const [showModal, setShowModal] = useState(false);

  /*** Constants ***/
  const dateRanges = useMemo(() => transformDateRanges(disabledDateRanges), [disabledDateRanges]);
  // Extract the unique `label` values from the `disabledDateRanges` prop.
  const disabledLabels = useMemo(
    () => [
      // The component default disabled all weekends, so we add that label here.
      translate("DATE_INPUT_WEEKEND_DISABLED_LABEL"),
      ...new Set(
        disabledDateRanges
          .map(({ label }) => label)
          // Filter out any undefined values
          .filter((label): label is string => label !== undefined),
      ),
    ],
    [disabledDateRanges],
  );
  // The chosen date formatted to "Month Day, Year"
  const formattedDate = formatDate({
    date: DateTime.fromISO(value ?? ""),
    format: "Jan 1, 2025",
  });

  /*** Render #1: Read Only ***/
  if (isReadOnly) {
    const buttonLabel = value ? formattedDate : translate("DATE_INPUT_DEFAULT_LABEL");

    return buttonLabel;
  }

  /*** Render #2: Disabled / Strike Through state ***/
  if (isStrikethrough || isDisabled) {
    return (
      <button
        css={{
          ...STYLES.button,
          width: isFluid ? "100%" : WIDTH,
          color: Colors.slate400,
          // Removed is an extra state that just adds a strike through style
          textDecorationLine: isStrikethrough ? "line-through" : "initial",
          // Show a disabled cursor when the button is disabled
          cursor: isDisabled || isStrikethrough ? "not-allowed !important" : "pointer !important",
        }}
        disabled
      >
        {value ? formattedDate : translate("DATE_INPUT_DEFAULT_LABEL")}
      </button>
    );
  }

  /*** Render #3: Inputs ***/
  return (
    <>
      <div
        css={{
          display: value ? "block" : "none",
          // Expand this parent to 100%
          width: isFluid ? "100%" : WIDTH,
          // Expand the shadow DOM button as well
          "ion-datetime-button::part(native)": {
            ...STYLES.button,
            margin: 0,
            width: isFluid ? "100%" : WIDTH,
          },
        }}
      >
        <IonDatetimeButton datetime={id} />
      </div>

      <button
        css={{
          ...STYLES.button,
          display: value ? "none" : "block",
          width: WIDTH,
          textAlign: "center",
        }}
        onClick={() => setShowModal(true)}
        disabled={isStrikethrough}
      >
        {translate("DATE_INPUT_DEFAULT_LABEL")}
      </button>

      <IonModal
        isOpen={showModal}
        // Dismisses the Modal when pressing "ESC" or clicking outside of the
        // modal
        canDismiss={true}
        // Triggered when dismissing the modal via the "ESC" key or clicking
        // outside of the modal
        onDidDismiss={() => setShowModal(false)}
        // Reset to default behavior when modal is about to present and no value exists
        onWillPresent={() => {
          if (!value && datetime.current) {
            datetime.current.reset();
          }
        }}
        // TODO: Review is this is needed... Currently if this is removed then
        // the side of the Modal is larger than the DatePicker.
        keepContentsMounted
        // Adds an additional backdrop and box shadow to the modal so that when
        // it's appearing over another modal it's easier to see.
        className="stackedModal"
      >
        <IonDatetime
          id={id}
          ref={datetime}
          value={value}
          min={dateRanges?.min}
          max={dateRanges?.max}
          presentation="date"
          isDateEnabled={(date: string) => {
            const withinDisabledDateRanges = isWithinDateRange(date, dateRanges.disabledDateRanges);

            return isWeekday(date) && !withinDisabledDateRanges;
          }}
          onIonChange={(e) => {
            onChange?.(
              // Purposely convert to ISO date so to ignore time and timezone
              // TODO: Why are we getting this TS error, maybe the DateTime
              // will not throw on invalid dates? So add the Luxon setting update.
              // Investigate in https://linear.app/swing-education/issue/MKTP-946
              DateTime.fromISO(e.detail.value as string).toISODate() as string,
            );
          }}
        >
          {/* Footer of the DatePicker */}
          <slot slot="buttons">
            {/* Container for the disclaimer and buttons */}
            <div
              css={{
                // The container must have a width since the slot have a width
                // of auto and cannot use "100%". This 300px is derived from
                // the IonModal being 320px with left/right padding of 10px.
                width: 300,
                paddingLeft: 6, // Since the component adds 10px padding
                paddingRight: 6, // Since the component adds 10px padding
                paddingBottom: 6, // Since the component adds 10px padding

                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {/* Disclaimer (only render when there are disabled ranges) */}
              <div
                css={{
                  backgroundColor: Colors.slate200,
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <p css={{ margin: 0, fontSize: 12, fontWeight: 400, lineHeight: "16px" }}>
                  {translate("DATE_INPUT_DISABLED_DAYS_DISCLAIMER", { disabledLabels })}
                </p>
              </div>

              {/* Buttons */}
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <button
                  css={{ ...STYLES.datePickerButton, color: Colors.red500 }}
                  onClick={() => datetime.current?.cancel(true)}
                >
                  {translate("DATE_INPUT_CANCEL_LABEL")}
                </button>
                <button
                  css={STYLES.datePickerButton}
                  onClick={() => datetime?.current?.confirm(true)}
                >
                  {translate("DATE_INPUT_DONE_LABEL")}
                </button>
              </div>
            </div>
          </slot>
        </IonDatetime>
      </IonModal>
    </>
  );
}

// Add props type for DatePickerModal
export type DatePickerModalProps = {
  /**
   * The ISO-8601 formatted date string (YYYY-MM-DD) to display as the selected value.
   * If undefined, no date is selected by default.
   *
   * @example
   * "2020-01-01"
   */
  value?: string;
  /**
   * Controls whether the modal is open (true) or closed (false).
   */
  isEnabled: boolean;
  /**
   * Array of date ranges to disable in the picker. Prevents selection of dates within these ranges.
   * See DisabledDateRanges type for details.
   */
  disabledDateRanges: DisabledDateRanges;
  /**
   * Optional label to display in the modal describing the disabled date ranges.
   * If not provided, no label is shown.
   */
  disabledLabel?: string;
  /**
   * Handler called when a date is selected. Receives the ISO-8601 date string (YYYY-MM-DD).
   *
   * @param datetime The selected date string.
   */
  onChange: (datetime: string) => void;
  /**
   * Optional handler called when the modal is closed/dismissed.
   */
  onClose?: () => void;
};

export function DatePickerModal({
  value,
  isEnabled,
  disabledDateRanges,
  disabledLabel,
  onChange,
  onClose,
}: DatePickerModalProps) {
  const id = useId();
  const datetime = useRef<null | HTMLIonDatetimeElement>(null);
  const dateRanges = useMemo(() => transformDateRanges(disabledDateRanges), [disabledDateRanges]);

  return (
    <IonModal
      isOpen={isEnabled}
      // Dismisses the Modal when pressing "ESC" or clicking outside of the
      // modal
      canDismiss={true}
      // Triggered when dismissing the modal via the "ESC" key or clicking outside of the modal
      onDidDismiss={() => {
        if (onClose) onClose();
      }}
      onWillPresent={() => {
        if (!value && datetime.current) {
          datetime.current.reset();
        }
      }}
      // TODO: Review is this is needed... Currently if this is removed then
      // the side of the Modal is larger than the DatePicker.

      className="ion-datetime-button-overlay"
    >
      <IonDatetime
        id={id}
        ref={datetime}
        value={value}
        min={dateRanges?.min}
        max={dateRanges?.max}
        presentation="date"
        isDateEnabled={(date: string) => {
          const withinDisabledDateRanges = isWithinDateRange(date, dateRanges.disabledDateRanges);
          return isWeekday(date) && !withinDisabledDateRanges;
        }}
        onIonChange={(e) => {
          onChange?.(
            // Purposely convert to ISO date so to ignore time and timezone
            // TODO: Why are we getting this TS error, maybe the DateTime
            // will not throw on invalid dates? So add the Luxon setting update.
            // Investigate in https://linear.app/swing-education/issue/MKTP-946
            DateTime.fromISO(e.detail.value as string).toISODate() as string,
          );
        }}
      >
        <slot slot="buttons">
          <div
            css={{
              width: 300,
              paddingLeft: 6, // Since the component adds 10px padding
              paddingRight: 6, // Since the component adds 10px padding
              paddingBottom: 6, // Since the component adds 10px padding

              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {disabledLabel && (
              <div
                css={{
                  backgroundColor: Colors.slate200,
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <p css={{ margin: 0, fontSize: 12, fontWeight: 400, lineHeight: "16px" }}>
                  {disabledLabel}
                </p>
              </div>
            )}

            {/* Buttons */}
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                css={{ ...STYLES.datePickerButton, color: Colors.red500 }}
                onClick={() => datetime.current?.cancel(true)}
              >
                {translate("DATE_INPUT_CANCEL_LABEL")}
              </button>
              <button
                css={STYLES.datePickerButton}
                onClick={() => datetime?.current?.confirm(true)}
              >
                {translate("DATE_INPUT_DONE_LABEL")}
              </button>
            </div>
          </div>
        </slot>
      </IonDatetime>
    </IonModal>
  );
}

/***** Styles *****/
// Setting the width for all buttons and IonDatetimeButton so that they are
// consistent when vertically aligned. Currently, due to not using monospace
// font they are slightly off.
const WIDTH = 130;
// Set a fixed height for the button so that both `button` and `IonDatetimeButton`
// are the same height.
const HEIGHT = 24;

const STYLES = {
  button: {
    height: HEIGHT,
    padding: "0 8px",
    borderRadius: 8,
    fontSize: 16,
    lineHeight: "24px",
    textAlign: "center",
    color: "black",
    marginLeft: "auto",
    alignItems: "center",
    background: Colors.slate200,
  },
  datePickerButton: {
    fontSize: 16,
    color: Colors.blue500,
    background: "transparent",
  },
} satisfies Record<string, CSSObject>;
